/**
 * Provider CA bundle upload (ADR-040 §D-4; REQ-PROVIDER-030; ADR-015 key
 * custody).
 *
 * **One kind of material, and public only.** The control accepts a provider's
 * Tier-1/2 CA bundle — the trust anchor deciding which clients this interface
 * will admit. It does not, and must not, accept a private key: Meridian's
 * keypair stays minted at install by ``tools/meridian_certgen.py`` and is
 * referenced by path, because a key routed through nginx, the BFF and
 * application memory cannot afterwards be *proven* uncopied, and key custody is
 * a documented control in ``security.md`` whose change would trigger
 * re-accreditation.
 *
 * ⚠ **There is no Meridian-certificate upload either**, unlike the egress
 * sibling. The provider-facing server identity is minted at install beside its
 * key and never passes a proving path (REQ-PROVIDER-035), so the BFF's route
 * takes no ``kind`` field at all — it accepts exactly one thing. The Meridian
 * certificate and key are therefore **path boxes with no upload control**, and
 * the editor says why rather than leaving the asymmetry to be noticed.
 *
 * # Enforced at upload, not merely echoed
 *
 * A CA bundle decides *who Meridian will accept mission data from*, so the BFF
 * validates the X.509 and **refuses** an anchor that is expired or is not a CA
 * — storing one silently produces an interface that refuses every provider
 * connection with no visible reason. What it accepts, it echoes: subject,
 * issuer, validity and SHA-256 fingerprint. This component surfaces that echo
 * prominently, because the point is that the operator verifies it **out of
 * band** against what the provider's operator told them, rather than trusting
 * the transfer.
 *
 * Cross-references:
 *   - ADR-040 §D-4 (per-interface trust); ADR-015 (cert material by path).
 *   - REQ-PROVIDER-030 (D-10-validated anchors), -035 (identity never uploadable).
 *   - Flow V Frame 7 (install).
 */
import { useRef, useState } from "react";
import {
  useUploadProviderTrustAnchor,
  type ProviderTrustAnchorUploadResult,
} from "@/providers/use-provider-interfaces";

/** Props for {@link ProviderCertUpload}. */
export interface ProviderCertUploadProps {
  /** Called with the server-generated path so the caller can set the reference field. */
  onStored: (storedPath: string) => void;
}

/** Render the upload button + the parsed-certificate echo. */
export function ProviderCertUpload({ onStored }: ProviderCertUploadProps): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<ProviderTrustAnchorUploadResult | null>(null);
  const mutation = useUploadProviderTrustAnchor();

  const handleFile = (file: File | undefined): void => {
    if (file === undefined) return;
    mutation.mutate(file, {
      onSuccess: (uploaded) => {
        setResult(uploaded);
        onStored(uploaded.stored_path);
      },
    });
  };

  return (
    <div className="flex flex-col gap-1" data-testid="provider-cert-upload">
      <input
        ref={inputRef}
        type="file"
        accept=".pem,.crt,.cer,application/x-pem-file"
        className="hidden"
        data-testid="provider-cert-file"
        onChange={(event) => handleFile(event.target.files?.[0])}
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={mutation.isPending}
          data-testid="provider-cert-upload-button"
          className="rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated disabled:opacity-50"
        >
          {mutation.isPending ? "Uploading…" : "Upload PEM…"}
        </button>
        <span className="text-meta text-text-muted">
          Public CA material only. An expired anchor, or one that is not a CA, is refused here.
        </span>
      </div>

      {mutation.isError && (
        <p role="alert" data-testid="provider-cert-error" className="text-meta text-destructive">
          {mutation.error.status === 503
            ? "This deployment has no writable cert volume — pre-stage the file and reference it by path instead."
            : `Rejected: ${describeRejection(mutation.error.body)}`}
        </p>
      )}

      {result !== null && (
        <div
          data-testid="provider-cert-result"
          className="rounded border border-border bg-bg-base p-2 text-meta"
        >
          <p className="pb-1 text-text-secondary">
            Stored as <span className="font-mono text-text-primary">{result.stored_path}</span>.
            Verify the fingerprint out of band before starting the interface.
          </p>
          <ul className="flex flex-col gap-1">
            {result.certificates.map((certificate) => (
              <li key={certificate.fingerprint_sha256} className="font-mono text-text-primary">
                <div>{certificate.subject}</div>
                <div className="text-text-secondary">issued by {certificate.issuer}</div>
                <div className="text-text-secondary">
                  {certificate.not_before} → {certificate.not_after}
                  {certificate.is_ca ? " · CA" : ""}
                </div>
                <div className="break-all text-text-secondary">
                  SHA-256 {certificate.fingerprint_sha256}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Pull the BFF's typed rejection reason out of an error body, without leaking internals. */
function describeRejection(body: unknown): string {
  if (typeof body === "object" && body !== null) {
    const message = (body as { message?: unknown }).message;
    if (typeof message === "string") return message;
    const detail = (body as { detail?: unknown }).detail;
    if (typeof detail === "object" && detail !== null) {
      const nested = (detail as { message?: unknown }).message;
      if (typeof nested === "string") return nested;
    }
  }
  return "the file is not valid certificate material.";
}
